/**
 * Generic, shape-agnostic helpers for planning multi-select filters (Schedule
 * and Dispatch). Module-specific option collectors and matchers stay in each
 * module's own filter model.
 */

/**
 * @typedef {{ value: string, label: string }} PlanningFilterOption
 */

/**
 * Merge already-selected options back into a cross-curated option list so that
 * curation only narrows the *available* choices and never hides selections
 * (which would drop their chips from the multi-select).
 *
 * @param {PlanningFilterOption[]} curatedOptions Options narrowed by the other filters.
 * @param {PlanningFilterOption[]} baseOptions Uncurated options for the same filter.
 * @param {Array<string>} selectedIds Currently selected ids for this filter.
 * @returns {PlanningFilterOption[]}
 */
export function mergeSelectedFilterOptions(curatedOptions, baseOptions, selectedIds) {
  if (!selectedIds?.length) {
    return curatedOptions;
  }
  const curatedValues = new Set(curatedOptions.map((option) => option.value));
  const selectedSet = new Set(selectedIds);
  const missingSelected = baseOptions.filter(
    (option) => selectedSet.has(option.value) && !curatedValues.has(option.value)
  );
  if (!missingSelected.length) {
    return curatedOptions;
  }
  return [...curatedOptions, ...missingSelected].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" })
  );
}

/**
 * Drop selected ids that are no longer present in the base option list (e.g.
 * the item left the visible date range). Selections are kept in state; only
 * the *effective* filter shrinks.
 *
 * @param {Array<string>} selectedIds
 * @param {PlanningFilterOption[]} options Base (uncurated) options for the filter.
 * @returns {Array<string>}
 */
export function getEffectiveFilterIds(selectedIds, options) {
  if (!selectedIds?.length) {
    return [];
  }
  const validIds = new Set(options.map((option) => option.value));
  return selectedIds.filter((id) => validIds.has(id));
}
